import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { SchoolSelect, useSchools } from "../components/SchoolSelect";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const COLORS = ["#0A5C36", "#B48E4B", "#9C4221", "#22577A", "#7C3AED"]; // eslint-disable-line no-unused-vars

export default function ReportsPage() {
  const { t, dir } = useI18n();
  const { schools, schoolId, setSchoolId, isSuper, queryId } = useSchools({ allowAll: true });
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = queryId ? `?school_id=${queryId}` : "";
    api.get(`/reports/summary${q}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [queryId]);

  if (!data) return <div className="p-8 text-muted-foreground">{t("no_data")}</div>;

  const attendanceData = [
    { name: t("present"), value: data.attendance.present, fill: "#0A5C36" },
    { name: t("absent"), value: data.attendance.absent, fill: "#9C4221" },
    { name: t("late"), value: data.attendance.late, fill: "#D4A017" },
    { name: t("excused"), value: data.attendance.excused, fill: "#22577A" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between flex-wrap gap-3 items-end">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("reports")}</h1>
          <p className="text-sm text-muted-foreground mt-2">{t("overview")}</p>
        </div>
        <SchoolSelect
          schools={schools}
          value={schoolId}
          onChange={setSchoolId}
          testId="reports-school-select"
          className="w-full sm:w-56"
          disabled={!isSuper}
          showAll={isSuper}
        />
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: "students_count", v: data.totals.students },
          { k: "teachers_count", v: data.totals.teachers },
          { k: "classes_count", v: data.totals.classes },
          { k: "open_invoices", v: data.totals.invoices_open },
        ].map(it => (
          <Card key={it.k} className="p-4 sm:p-5 border shadow-none">
            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t(it.k)}</div>
            <div className="text-2xl sm:text-3xl font-semibold mt-2 font-mono">{it.v}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance distribution */}
        <Card className="p-4 sm:p-6 border shadow-none">
          <div className="flex justify-between items-baseline mb-4">
            <h3 className="text-lg font-medium">{t("attendance_distribution")}</h3>
            <span className="text-xs text-muted-foreground">30d</span>
          </div>
          <div className="h-72" data-testid="chart-attendance">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={attendanceData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {attendanceData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Students per grade */}
        <Card className="p-4 sm:p-6 border shadow-none">
          <h3 className="text-lg font-medium mb-4">{t("students_per_grade")}</h3>
          <div className="h-72" data-testid="chart-grade-students">
            <ResponsiveContainer>
              <BarChart data={data.students_per_grade} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} reversed={dir === "rtl"} />
                <YAxis tick={{ fontSize: 12 }} orientation={dir === "rtl" ? "right" : "left"} />
                <Tooltip />
                <Bar dataKey="count" fill="#0A5C36" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Finance: collected vs outstanding */}
        <Card className="p-4 sm:p-6 border shadow-none">
          <h3 className="text-lg font-medium mb-4">{t("finance_overview")}</h3>
          <div className="h-72" data-testid="chart-finance">
            <ResponsiveContainer>
              <BarChart data={data.finance} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} reversed={dir === "rtl"} />
                <YAxis tick={{ fontSize: 12 }} orientation={dir === "rtl" ? "right" : "left"} />
                <Tooltip />
                <Legend />
                <Bar dataKey="collected" name={t("collected")} fill="#0A5C36" radius={[6, 6, 0, 0]} />
                <Bar dataKey="outstanding" name={t("outstanding")} fill="#9C4221" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Behavior trend */}
        <Card className="p-4 sm:p-6 border shadow-none">
          <h3 className="text-lg font-medium mb-4">{t("behavior_trend")}</h3>
          <div className="h-72" data-testid="chart-behavior">
            <ResponsiveContainer>
              <LineChart data={data.behavior_trend} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} reversed={dir === "rtl"} />
                <YAxis tick={{ fontSize: 12 }} orientation={dir === "rtl" ? "right" : "left"} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="positive" name={t("positive")} stroke="#0A5C36" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="negative" name={t("negative")} stroke="#9C4221" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
